import React, { Component } from "react";
import { Button } from "react-bootstrap";
import { GoPencil, GoSync } from "react-icons/go";
import { LuClipboardCopy, LuCheck } from "react-icons/lu";
import Tooltip from "../../../controls/common/tooltip";
import Clipboard from "../../../controls/common/clipboard";
import EditMessageContentModal from "../../../editor/details/content";
import { canMutateSession } from "../../session/mutationGate";
import Utils from "context/utils";

// Header action bar for envelope-backed playground widgets (Thread, Message).
// Renders Edit / Refresh / Copy controls for the widget's bound session object
// and owns the edit modal. Reuses the existing EditMessageContentModal
// (type-aware for message vs. thread) and SessionManager methods, so there is
// no new persistence path.
//
// Props:
//   alias          - bound session-object alias (string|null)
//   value          - current envelope value (object|null when not loaded)
//   shellInterface - this.props.data.shellInterface from the widget
//   sessionManager - the SessionContext value (live SessionManager)
//   refreshTooltip - label for the refresh control (default "Refresh object");
//                    the Thread widget passes "Refresh thread"
//   onRefresh      - optional callback invoked in addition to reloading the
//                    session object. Widgets whose displayed data lives outside
//                    the envelope value (e.g. the Thread's messages, fetched via
//                    a separate API) use this to reload that data too.
export default class EnvelopeControls extends Component {
  constructor(props) {
    super(props);
    this.state = {
      editModalVisible: false,
      // Snapshot of the envelope taken when Edit is opened, mirroring how
      // DetailsPane stores a deepCopy before handing it to the modal.
      editObjectValue: null,
      copied: false
    };
    this.copyTimer = null;
  }

  componentWillUnmount() {
    if ( this.copyTimer ) clearTimeout(this.copyTimer);
  }

  // Edit uploads the envelope (sessionManager.uploadSessionObject) — a session-data mutation — so it
  // requires both an editable revision AND an open session. See session/mutationGate.js for the shared
  // fail-open default applied when a shellInterface doesn't expose the full seam.
  canEdit() {
    return canMutateSession(this.props.shellInterface);
  }

  // Tooltip copy for the Edit button. Edit only renders while mutations are possible, so there is no
  // blocked state to explain — mirrors ThreadNode.sendTooltipText() / ChatNode.sendTooltipText().
  editTooltipText() {
    return "Edit object";
  }

  hasValue() {
    return !!this.props.value;
  }

  openEditModal = (e) => {
    if ( e ) e.stopPropagation();
    this.setState({
      editModalVisible: true,
      editObjectValue: Utils.deepCopy(this.props.value)
    });
  };

  closeEditModal = () => {
    this.setState({ editModalVisible: false, editObjectValue: null });
  };

  handleSave = (newValue) => {
    const { sessionManager, alias } = this.props;
    if ( sessionManager && alias ) {
      sessionManager.uploadSessionObject(alias, newValue);
    }
  };

  handleRefresh = (e) => {
    if ( e ) e.stopPropagation();
    const { sessionManager, alias, onRefresh } = this.props;
    if ( sessionManager && alias ) {
      sessionManager.loadSessionObject(alias);
    }
    // Reload data that lives outside the envelope value (e.g. the Thread's
    // messages). loadSessionObject only refreshes the wrapper value; without
    // this, a Thread refresh would leave the displayed messages stale.
    if ( onRefresh ) onRefresh();
  };

  handleCopy = (e) => {
    if ( e ) e.stopPropagation();
    if ( !this.hasValue() ) return;
    Clipboard.to(JSON.stringify(this.props.value, null, 2));
    this.setState({ copied: true });
    if ( this.copyTimer ) clearTimeout(this.copyTimer);
    this.copyTimer = setTimeout(() => this.setState({ copied: false }), 1200);
  };

  render() {
    const { alias, shellInterface } = this.props;
    if ( !alias ) return null;

    // Hidden whenever mutations are blocked, for ANY reason — no write access, a committed revision,
    // or a closed session. This used to distinguish them, hiding Edit for a read-only viewer but
    // leaving it disabled-with-a-tooltip for a closed session; every other playground mutation
    // surface hides, so Edit hides too. The reason lives in the details pane's Mutations row.
    const showEdit = this.canEdit() && this.hasValue();
    const showCopy = this.hasValue();
    const refreshTooltip = this.props.refreshTooltip || "Refresh object";

    return (
      <span
        className="playground-widget-controls"
        onClick={(e) => e.stopPropagation()}
      >
        {showEdit &&
          <Tooltip text={this.editTooltipText()}>
            <Button
              variant="light"
              size="sm"
              aria-label="Edit object"
              onClick={this.openEditModal}
            >
              <GoPencil />
            </Button>
          </Tooltip>
        }
        <Tooltip text={refreshTooltip}>
          <Button variant="light" size="sm" aria-label={refreshTooltip} onClick={this.handleRefresh}>
            <GoSync />
          </Button>
        </Tooltip>
        {showCopy &&
          <Tooltip text={this.state.copied ? "Copied" : "Copy object"}>
            <Button variant="light" size="sm" onClick={this.handleCopy}>
              {this.state.copied ? <LuCheck /> : <LuClipboardCopy />}
            </Button>
          </Tooltip>
        }
        {this.state.editModalVisible &&
          <EditMessageContentModal
            show={this.state.editModalVisible}
            portValue={this.state.editObjectValue}
            onSave={this.handleSave}
            onHide={this.closeEditModal}
            shell={{ shellInterface }}
          />
        }
      </span>
    );
  }
}
